import Raven from 'raven-js';
import ReactGA from 'react-ga';
import debugFactory from 'debug';
import { getUserInfo } from '../rest/securityAccessRest';
import { purgeAllPersistStore } from '../offline/persistStoreProvider';
import attachments from '../../attachments/attachments';

const debug = debugFactory('authDucks');

// Actions
const USER_INFO_START = 'USER_INFO_START';
export const USER_INFO_SUCCESS = 'USER_INFO_SUCCESS';
export const USER_INFO_SUCCESS_OFFLINE = 'USER_INFO_SUCCESS_OFFLINE';
export const USER_INFO_FAILURE = 'USER_INFO_FAILURE';

export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const LOGOUT_FAILURE = 'LOGOUT_FAILURE';

const initialSecurityContext = {
  authenticated: false,
  username: '',
  firstName: '',
  lastName: '',
  regionId: null,
  regionName: null,
  agencyId: null,
  agencyName: null,
  functionId: null,
  active: false,
  email: '',
  profiles: [],
  roles: [],
};

// Reducer
const securityContext = (state = initialSecurityContext, action) => {
  switch (action.type) {
    case USER_INFO_START: {
      return state;
    }
    case USER_INFO_SUCCESS: {
      Raven.setUserContext({ email: action.payload.email });
      ReactGA.set({
        userId: action.payload.username,
        dimension1: action.payload.functionName,
        dimension2: action.payload.regionName,
        dimension3: action.payload.agencyName,
      });

      return {
        ...state,
        authenticated: true,
        username: action.payload.username,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        regionId: action.payload.regionId,
        regionName: action.payload.regionName,
        agencyName: action.payload.agencyName,
        agencyId: action.payload.agencyId,
        functionId: action.payload.functionId,
        active: action.payload.active,
        profiles: action.payload.profiles,
        roles: action.payload.roles,
      };
    }
    case USER_INFO_SUCCESS_OFFLINE: {
      Raven.setUserContext({ email: state.email });

      return state;
    }
    case LOGOUT_SUCCESS: {
      return {
        ...state,
        ...initialSecurityContext,
      };
    }
    default:
      return state;
  }
};
export default securityContext;

// Action creators

/*
 * ==================
 * LOGOUT
 * ==================
 */

export const clearStorage = () => (
  // Clear IndexedDB
  purgeAllPersistStore()
    .then(attachments.clearAttachments)
    .then(() => {
      debug('redux-persist cleared successfully');
    })
);

/**
 * The action that triggers a HTTP request to logout.
 *
 * If the logout is successful, an action is dispatched to clean the data
 * persisted in the browser. The same treatment is performed if the user
 * is offline.
 *
 * If there was an error during logout, an action is dispatched.
 */
export const logoutStart = () => (
  (dispatch) => {
    dispatch({
      type: LOGOUT_SUCCESS,
      payload: {},
    });

    /*
     * Resets the Redux authentication state and deletes all the data
     * data persisted in the browser.
     */
    const postSamlLogout = () => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/saml/logout';
      document.body.appendChild(form);
      form.submit();
    };

    clearStorage().then(() => {
      /*
       * The user is unauthenticated. The window location is set to a
       * patch that is not accessible to the user. Consequently, it will
       * trigger the authentication process.
       */
      postSamlLogout();
    }).catch((err) => {
      console.log('Error during logout', err);
      postSamlLogout();
    });
  }
);

/**
 * ========================
 * Authentication process
 * ========================
 */

/**
 * Clear storage and redirect to the login page.
 * (Not actually an action creator)
 */
export const startAuthentication = () => {
  /*
   * Resets the Redux authentication state and deletes all the data
   * data persisted in the browser.
   */
  clearStorage().then(() => {
    /*
     * The user is unauthenticated. The window location is set to a
     * patch that is not accessible to the user. Consequently, it will
     * trigger the authentication process.
     */
    window.location = '/api/auth/start';
  }).catch((err) => {
    console.log('Error during cleaning data', err);
    window.location = '/api/auth/start';
  });
};

/**
 * ==================
 * User information
 * ==================
 */

/**
 * Dispatches the information that getting the user information
 * from the server has failed.
 *
 * If it failed with a 401 HTTP status code. This means that :
 * 1 - the user should be logged out,
 * 2 - we are online.
 */
export const userInfoFailure = responseStatus => (
  (dispatch) => {
    dispatch({
      type: USER_INFO_FAILURE,
    });
    if (responseStatus === 401) {
      // The user has lost their permissions - cleanup and redirect to login page
      startAuthentication();
    }
  }
);

/**
 * User information was just loaded from the server. Dispatches this
 * information to store it into the Redux store.
 *
 * @param userInfo The JavaScript object representing the user.
 */
export const userInfoSuccess = userInfo => ({
  type: USER_INFO_SUCCESS,
  payload: userInfo,
});

/**
 * The action that triggers a HTTP request to get the user information
 * from the server.
 *
 * If the user details are successfully loaded, an action is dispatched
 * to store this data into the Redux store.
 *
 * If there is an error, an appropriate action is dispatched. If this is
 * a 401 HTTP status code, the user is logout.
 */
export const userInfoStart = () => (
  (dispatch) => {
    if (!navigator.onLine) {
      // Dont try to load the user info if offline
      dispatch({ type: USER_INFO_SUCCESS_OFFLINE });
      return;
    }

    dispatch({
      type: USER_INFO_START,
    });

    getUserInfo().then((response) => {
      if (response.ok) {
        /*
         * The user details were successfully loaded.
         */
        response.json().then((userInfo) => {
          dispatch(userInfoSuccess(userInfo));
        });
      } else {
        /*
         * There was an error. It should be managed.
         */
        dispatch(userInfoFailure(response.status));
      }
    }, () => {
      /*
       * We are offline.
       */
      dispatch({ type: USER_INFO_SUCCESS_OFFLINE });
    });
  }
);




// WEBPACK FOOTER //
// ./book/src/commons/security/authDucks.js